/**
 * React Query hooks for the Guidance surfaces.
 *
 * Prerequisites are deliberately **not** cached for long. They are evaluated live on the
 * server against the user's permissions and the tool's current state, and a pre-start
 * panel showing a stale pass would let somebody start a walkthrough that cannot succeed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { GuideEdit, PassReason, RecordingPayload } from '../types';
import {
  beginGuideEdit,
  completeWalkthrough,
  endWalkthrough,
  fetchGmsAccess,
  fetchGuideEditor,
  fetchGuideScreenshot,
  fetchHelpContext,
  fetchLibrary,
  fetchScreens,
  fetchTopic,
  fetchWalkthrough,
  passStep,
  publishGuide,
  publishRevision,
  recordGuide,
  replaceGuideScreenshot,
  restartWalkthrough,
  rollbackTopic,
  saveFeedback,
  startWalkthrough,
  transitionRevision,
  updateGuide,
} from '../services/gms-service';

/** Capability flags. Cached like the other `my-access` calls in this app. */
export function useGmsAccess() {
  return useQuery({
    queryKey: ['gms', 'access'],
    queryFn: ({ signal }) => fetchGmsAccess(signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Screens with guides — what the header button matches the current route against.
 *
 * Fetched once per session and only for someone in the rollout: `enabled` waits for
 * `can_view`, so a user outside the pilot never sends a request that would 403.
 */
export function useGmsScreens(enabled: boolean) {
  return useQuery({
    queryKey: ['gms', 'screens'],
    queryFn: ({ signal }) => fetchScreens(signal),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** What Help is relevant here. Enabled only once a tool is known. */
export function useHelpContext(tool: string | null, location?: string) {
  return useQuery({
    queryKey: ['gms', 'context', tool, location ?? null],
    queryFn: ({ signal }) => fetchHelpContext(tool as string, location, signal),
    enabled: Boolean(tool),
    staleTime: 60 * 1000,
  });
}

/** One topic's content. Enabled only while its panel is open. */
export function useTopic(toolKey: string | null, topicKey: string | null) {
  return useQuery({
    queryKey: ['gms', 'topic', toolKey, topicKey],
    queryFn: ({ signal }) => fetchTopic(toolKey as string, topicKey as string, signal),
    enabled: Boolean(toolKey && topicKey),
  });
}

/**
 * The pre-start panel.
 *
 * `staleTime: 0` on purpose: prerequisites reflect live permissions and live tool state,
 * and this panel is what decides whether Start is offered.
 */
export function useWalkthrough(
  toolKey: string | null,
  topicKey: string | null,
  contextIds: Record<string, number> = {},
  /** A specific version — an author previewing a draft (G18). Omit for the live one. */
  version?: number
) {
  return useQuery({
    queryKey: ['gms', 'walkthrough', toolKey, topicKey, contextIds, version ?? null],
    queryFn: ({ signal }) =>
      fetchWalkthrough(toolKey as string, topicKey as string, contextIds, signal, version),
    enabled: Boolean(toolKey && topicKey),
    staleTime: 0,
  });
}

/** Start, restart, end and complete, each invalidating the panel that shows state. */
export function useWalkthroughActions(toolKey: string, topicKey: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ['gms', 'walkthrough', toolKey, topicKey],
    });

  const start = useMutation({
    mutationFn: (variables: {
      contextIds?: Record<string, number>;
      isPreview?: boolean;
      version?: number;
    }) =>
      startWalkthrough(
        toolKey,
        topicKey,
        variables.contextIds ?? {},
        variables.isPreview ?? false,
        variables.version
      ),
    onSuccess: invalidate,
  });

  const restart = useMutation({
    mutationFn: (contextIds: Record<string, number> = {}) =>
      restartWalkthrough(toolKey, topicKey, contextIds),
    onSuccess: invalidate,
  });

  const end = useMutation({
    mutationFn: (reason: string = '') => endWalkthrough(toolKey, topicKey, reason),
    onSuccess: invalidate,
  });

  const complete = useMutation({
    mutationFn: (signal: string) => completeWalkthrough(toolKey, topicKey, signal),
    onSuccess: invalidate,
  });

  // Move past a step without its action; the server checks the reason (G23).
  const pass = useMutation({
    mutationFn: (variables: {
      stepKey: string;
      reason: PassReason;
      contextIds?: Record<string, number>;
    }) =>
      passStep(toolKey, topicKey, variables.stepKey, variables.reason, variables.contextIds),
    onSuccess: invalidate,
  });

  return { start, restart, end, complete, pass };
}

/** One editable rating per user per version. */
export function useSaveFeedback(toolKey: string, topicKey: string) {
  return useMutation({
    mutationFn: (variables: { rating: number; comment: string }) =>
      saveFeedback(toolKey, topicKey, variables.rating, variables.comment),
  });
}

/** One guide's live and draft state for Review & Edit (G18). */
export function useGuideEditor(toolKey: string, topicKey: string) {
  return useQuery({
    queryKey: ['gms', 'guide-editor', toolKey, topicKey],
    queryFn: ({ signal }) => fetchGuideEditor(toolKey, topicKey, signal),
    enabled: Boolean(toolKey && topicKey),
  });
}

/**
 * Review & Edit writes. Each one returns the whole guide, which replaces the cached
 * copy; the library and every walkthrough panel are invalidated because a publish
 * changes what they show.
 */
export function useGuideEditorActions(toolKey: string, topicKey: string) {
  const queryClient = useQueryClient();
  const key = ['gms', 'guide-editor', toolKey, topicKey];
  const settle = (payload: unknown) => {
    queryClient.setQueryData(key, payload);
    void queryClient.invalidateQueries({ queryKey: ['gms', 'library'] });
    void queryClient.invalidateQueries({ queryKey: ['gms', 'walkthrough', toolKey, topicKey] });
    void queryClient.invalidateQueries({ queryKey: ['gms', 'screens'] });
  };

  const begin = useMutation({
    mutationFn: () => beginGuideEdit(toolKey, topicKey),
    onSuccess: settle,
  });

  const save = useMutation({
    mutationFn: (variables: { expectedRevision: number; edit: GuideEdit }) =>
      updateGuide(toolKey, topicKey, variables.expectedRevision, variables.edit),
    onSuccess: settle,
  });

  const screenshot = useMutation({
    mutationFn: (variables: { stepKey: string; expectedRevision: number; file: File }) =>
      replaceGuideScreenshot(
        toolKey,
        topicKey,
        variables.stepKey,
        variables.expectedRevision,
        variables.file
      ),
    onSuccess: settle,
  });

  const publish = useMutation({
    mutationFn: (variables: { expectedTopicRevision: number; note?: string }) =>
      publishGuide(toolKey, topicKey, variables.expectedTopicRevision, variables.note),
    onSuccess: settle,
  });

  // Submit, approve, reject: the existing revision transitions, with the reviewer's
  // comment. They return nothing, so the guide is refetched rather than replaced.
  const transition = useMutation({
    mutationFn: (variables: {
      revisionId: number;
      action: 'submit' | 'approve' | 'reject';
      comment: string;
      /** Approve only: the approver checked every screenshot for personal details. */
      screenshotsChecked?: boolean;
    }) =>
      transitionRevision(
        variables.revisionId,
        variables.action,
        variables.comment,
        variables.screenshotsChecked
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['gms', 'library'] });
    },
  });

  return { begin, save, screenshot, publish, transition };
}

/**
 * Upload a recording (G19). The library and the screens list change — a recording may
 * add a screen — so both are refetched.
 */
export function useRecordGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      recording: RecordingPayload;
      screenshots: Record<string, File>;
    }) => recordGuide(variables.recording, variables.screenshots),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gms', 'library'] });
      void queryClient.invalidateQueries({ queryKey: ['gms', 'screens'] });
    },
  });
}

/**
 * A draft step's screenshot bytes, while the blur tool is open on it. Keyed by the
 * current URL too, so a replaced screenshot is fetched afresh. Never cached for long:
 * it may show live data until it is blurred.
 */
export function useGuideScreenshot(
  toolKey: string,
  topicKey: string,
  stepKey: string | null,
  screenshotUrl: string | null
) {
  return useQuery({
    queryKey: ['gms', 'guide-screenshot', toolKey, topicKey, stepKey, screenshotUrl],
    queryFn: ({ signal }) => fetchGuideScreenshot(toolKey, topicKey, stepKey as string, signal),
    enabled: Boolean(stepKey && screenshotUrl),
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * The whole guidance library — every topic with its latest revision. Fetched once and
 * filtered in the page, so the status tabs can show counts without a request each.
 */
export function useGuidanceLibrary() {
  return useQuery({
    queryKey: ['gms', 'library'],
    queryFn: ({ signal }) => fetchLibrary(undefined, signal),
  });
}

/** Lifecycle actions on a reference topic's revision, each refreshing the library. */
export function useLibraryActions() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['gms', 'library'] });

  const transition = useMutation({
    mutationFn: (variables: { revisionId: number; action: 'submit' | 'approve' | 'reject' }) =>
      transitionRevision(variables.revisionId, variables.action),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    mutationFn: (variables: { revisionId: number; topicRevision: number }) =>
      publishRevision(variables.revisionId, variables.topicRevision),
    onSuccess: invalidate,
  });
  const rollback = useMutation({
    mutationFn: (variables: { toolKey: string; topicKey: string }) =>
      rollbackTopic(variables.toolKey, variables.topicKey),
    onSuccess: invalidate,
  });
  return { transition, publish, rollback, refresh: invalidate };
}
